import { useTranslation } from 'react-i18next'

import { type SegmentedControlOption } from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import {
  TARGET_DATE_SEGMENTS,
  TARGET_DATE_SEGMENT_I18N_KEY,
  type TargetDateSegmentKey,
} from '~/pages/DemandForecastList/models/targetDateSegment'

type Props = {
  /**
   * 現在アクティブな区分。過去日等どの区分にも該当しない場合は undefined
   * (= 全ボタン非 active、design コメント「対象日が当日より前 (過去) = 枠外」)。
   */
  value: TargetDateSegmentKey | undefined
  onChange: (segment: TargetDateSegmentKey) => void
}

/**
 * 対象日 segmented control (design/demand/forecast-list.html L113 `.ecl-segmented`):
 * 単日タブの date picker + `<` `>` の右に置く 当日 / 翌日 / 翌々日 / 翌々日以降 の単一選択。
 * date picker / `<` `>` とは呼び出し側 (FilterBar → DemandForecastListPage) が
 * targetDate state 経由で双方向連動させる (本 component は値の表示 + クリック時の通知を担う)。
 */
export const TargetDateSegment = ({
  value,
  onChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const options: Array<SegmentedControlOption<TargetDateSegmentKey>> =
    TARGET_DATE_SEGMENTS.map((segment) => ({
      value: segment,
      label: t(TARGET_DATE_SEGMENT_I18N_KEY[segment]),
    }))

  return (
    <AccessibleSegmentedControl
      aria-label={t(
        'page:demandForecastList.filter.targetDateSegmentGroupLabel',
      )}
      name="demand-forecast-target-date"
      onSelect={onChange}
      options={options}
      // DS の型は `selected: T` (required) だが、 実体は `selected ?? defaultValue` を
      // `findIndex` の等値比較にしか使わない (ecloud 0.14.2 dist 実装で確認済み)。
      // undefined を渡すと index が -1 = 全セグメント非選択で描画され、 これは
      // 「対象日が当日より前 (過去) = 枠外」の design 要件そのものなので意図どおり。
      // DS 側の型が `selected?: T` に緩和されたらこの cast は外せる。
      selected={value as TargetDateSegmentKey}
    />
  )
}
